// ─── Internal "What's Been Updated" changelog ────────────────────────────
// Deep-link-only internal page (/internal/updates) for masters/superusers —
// intentionally NO sidebar/nav entry. Entries from the last 3 months are
// grouped into COLLAPSIBLE WEEKLY DATE RANGES that END on the Friday 9:30 AM PT
// digest check (Friday→Friday windows, shared with the digest sender so the
// boundaries match exactly; most recent range on top and expanded by default;
// older ranges collapsed). Within each week they are
// grouped BY FEATURE (most recently active feature first); highlights render as
// cards and fixes as a compact collapsible list. There is no manual quick-add
// or commit-scan UI: entries are AI-drafted + auto-published from delivered
// commits. The weekly Slack digest posts every Friday at 9:30 AM PT; masters
// can reset its last-sent marker here to re-send a completed week.

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Wrench,
  Loader2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Clock,
} from "lucide-react";
import {
  INTERNAL_UPDATE_FEATURES,
  coerceFeature,
  type InternalUpdateFeature,
} from "@shared/internal-update-features";
import {
  digestRange,
  digestWeekForDate,
  isoDateInTz,
  DIGEST_TZ,
  type WeekRange,
} from "@shared/internal-updates-digest-window";
import type { InternalUpdate } from "@shared/schema";

interface FeedResponse {
  updates: InternalUpdate[];
  settings: {
    slackChannelId: string | null;
    slackChannelName: string | null;
    lastDigestWeekStart: string | null;
  } | null;
  slackConnected: boolean;
  slackWebhookMode: boolean;
}

// Only the last 3 months of changes are shown on the page.
const RETENTION_MONTHS = 3;

// Sortable timestamp for an entry (newest first). Missing dates sort last.
function entryTime(u: InternalUpdate): number {
  return u.createdAt ? new Date(u.createdAt).getTime() : 0;
}

// Human date range for a digest week, ENDING on its Friday 9:30 AM PT check —
// e.g. "Jul 3 – Jul 10, 2026". Formatted in PT (the digest's timezone) so the
// boundary lands on the exact Friday the digest checks, regardless of the
// viewer's local timezone. The year is shown on the end (and on the start too
// when the window straddles a year boundary).
function formatWeekRange(range: WeekRange): string {
  const sameYear =
    isoDateInTz(range.start, DIGEST_TZ).slice(0, 4) ===
    isoDateInTz(range.end, DIGEST_TZ).slice(0, 4);
  const fmt = (d: Date, withYear: boolean) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: DIGEST_TZ,
      ...(withYear ? { year: "numeric" } : {}),
    });
  return `${fmt(range.start, !sameYear)} – ${fmt(range.end, true)}`;
}

interface FeatureGroup {
  feature: InternalUpdateFeature;
  key: string;
  mostRecent: number;
  highlights: InternalUpdate[];
  fixes: InternalUpdate[];
}

// Group entries by feature. A feature's position is driven by its MOST RECENT
// entry (most-recently-active feature first); within a feature, highlights and
// fixes each stay newest-first. Ties fall back to the curated taxonomy order.
function groupByFeature(items: InternalUpdate[]): FeatureGroup[] {
  const map = new Map<InternalUpdateFeature, InternalUpdate[]>();
  for (const u of items) {
    const feature = coerceFeature(u.feature, u.description);
    const bucket = map.get(feature);
    if (bucket) bucket.push(u);
    else map.set(feature, [u]);
  }
  return Array.from(map.entries())
    .map(([feature, arr]) => {
      const sorted = [...arr].sort((a, b) => entryTime(b) - entryTime(a));
      return {
        feature,
        key: feature,
        mostRecent: sorted.length ? entryTime(sorted[0]) : 0,
        highlights: sorted.filter((i) => i.type === "highlight"),
        fixes: sorted.filter((i) => i.type === "fix"),
      };
    })
    .sort((a, b) => {
      if (b.mostRecent !== a.mostRecent) return b.mostRecent - a.mostRecent;
      return (
        INTERNAL_UPDATE_FEATURES.indexOf(a.feature) -
        INTERNAL_UPDATE_FEATURES.indexOf(b.feature)
      );
    });
}

export default function InternalUpdatesPage() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [collapsedFixes, setCollapsedFixes] = useState<Record<string, boolean>>({});
  // User overrides for week collapse state. Default (no override): the most
  // recent week is expanded, older weeks collapsed.
  const [weekOverrides, setWeekOverrides] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery<FeedResponse>({
    queryKey: ["/api/internal-updates"],
  });

  // Header status dates, straight from the shared Friday-9:30-PT digest-window
  // module (single source of truth — no ad-hoc date math). "Last updated" is
  // the most recent Friday 9:30 AM PT cutoff already passed; "Next update" is
  // the upcoming one. digestRange(now, "current") is exactly that window.
  const cutoffs = useMemo(() => {
    const range = digestRange(new Date(), "current");
    return { last: range.start, next: range.end };
  }, []);
  const fmtCutoff = (d: Date) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: DIGEST_TZ,
    }) + " 9:30 AM PT";

  const patchMutation = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; type?: string; description?: string; status?: string }) =>
      apiRequest("PATCH", `/api/internal-updates/${id}`, body),
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/internal-updates"] });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't update entry", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/internal-updates/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/internal-updates"] }),
    onError: (e: any) =>
      toast({ title: "Couldn't delete entry", description: e.message, variant: "destructive" }),
  });

  const published = useMemo(
    () => (data?.updates || []).filter((u) => u.status === "published"),
    [data],
  );

  // Keep only the last 3 months, then bucket into the SAME Friday→Friday windows
  // the Slack digest uses: each window ENDS on its Friday 9:30 AM PT check, so an
  // entry logged before a Friday cutoff falls into the week ending that Friday.
  // Keyed by the PT weekStartIso (also the digest's idempotency marker). Most
  // recent week first; each week's entries are grouped by feature.
  const weekGroups = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - RETENTION_MONTHS);
    cutoffDate.setHours(0, 0, 0, 0);
    const cutoff = cutoffDate.getTime();

    const recent = published.filter((u) => entryTime(u) >= cutoff);
    const byWeek = new Map<string, { range: WeekRange; items: InternalUpdate[] }>();
    for (const u of recent) {
      const range = digestWeekForDate(new Date(entryTime(u)));
      const bucket = byWeek.get(range.weekStartIso);
      if (bucket) bucket.items.push(u);
      else byWeek.set(range.weekStartIso, { range, items: [u] });
    }
    const nowRange = digestWeekForDate(new Date());
    // The window immediately before the current one (just before its Friday
    // cutoff) — labelled "Last week".
    const lastWeekIso = digestWeekForDate(
      new Date(nowRange.start.getTime() - 1),
    ).weekStartIso;
    return Array.from(byWeek.values())
      .sort((a, b) => (a.range.weekStartIso < b.range.weekStartIso ? 1 : -1))
      .map(({ range, items }) => ({
        weekStart: range.start.getTime(),
        key: range.weekStartIso,
        relative:
          range.weekStartIso === nowRange.weekStartIso
            ? "This week"
            : range.weekStartIso === lastWeekIso
              ? "Last week"
              : null,
        range: formatWeekRange(range),
        total: items.length,
        featureGroups: groupByFeature(items),
      }));
  }, [published]);

  const startEdit = (u: InternalUpdate) => {
    setEditingId(u.id);
    setEditText(u.description);
  };

  const renderEditor = (u: InternalUpdate) => (
    <div className="flex-1 space-y-2">
      <Textarea
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        rows={2}
        data-testid={`input-edit-update-${u.id}`}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => patchMutation.mutate({ id: u.id, description: editText.trim() })}
          disabled={!editText.trim() || patchMutation.isPending}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-page-title">
            What's Been Updated
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Internal changelog from the last 3 months, grouped into weekly ranges that end on the
            Friday 9:30 AM PT digest check, then by feature. A digest of each week's highlights posts
            to Slack every Friday at 9:30 AM PT.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2">
            <span className="inline-flex items-center gap-1" data-testid="text-last-updated">
              <Clock className="h-3.5 w-3.5" />
              Last updated: {fmtCutoff(cutoffs.last)}
            </span>
            <span data-testid="text-next-update">Next update: {fmtCutoff(cutoffs.next)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : weekGroups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Nothing logged in the last 3 months. Entries are auto-published from delivered commits.
            </CardContent>
          </Card>
        ) : (
          weekGroups.map((week, wi) => {
            const collapsed = weekOverrides[week.key] ?? wi !== 0;
            return (
              <div
                key={week.key}
                className="border rounded-lg overflow-hidden"
                data-testid={`section-week-${week.key}`}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                  onClick={() =>
                    setWeekOverrides((s) => ({ ...s, [week.key]: !(s[week.key] ?? wi !== 0) }))
                  }
                  data-testid={`button-toggle-week-${week.key}`}
                >
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  )}
                  <div className="flex items-baseline gap-2 flex-1 min-w-0">
                    {week.relative && (
                      <span className="text-sm font-semibold">{week.relative}</span>
                    )}
                    <span
                      className={
                        week.relative
                          ? "text-xs text-muted-foreground"
                          : "text-sm font-semibold"
                      }
                      data-testid={`text-week-range-${week.key}`}
                    >
                      {week.range}
                    </span>
                  </div>
                  <Badge variant="secondary" data-testid={`badge-week-count-${week.key}`}>
                    {week.total}
                  </Badge>
                </button>

                {!collapsed && (
                  <div className="p-4 space-y-8">
                    {week.featureGroups.map((group) => {
                      const fixKey = `${week.key}::${group.key}`;
                      return (
                        <div
                          key={fixKey}
                          data-testid={`section-feature-${week.key}-${group.key}`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <h2
                              className="text-sm font-semibold uppercase tracking-wide"
                              data-testid={`text-feature-${week.key}-${group.key}`}
                            >
                              {group.feature}
                            </h2>
                            <Badge
                              variant="secondary"
                              data-testid={`badge-feature-count-${week.key}-${group.key}`}
                            >
                              {group.highlights.length + group.fixes.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {group.highlights.map((u) => (
                              <div
                                key={u.id}
                                className="group flex items-start gap-3 rounded-md border bg-card p-3"
                                data-testid={`card-update-${u.id}`}
                              >
                                <Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                                {editingId === u.id ? (
                                  renderEditor(u)
                                ) : (
                                  <>
                                    <p className="text-sm flex-1" data-testid={`text-update-${u.id}`}>
                                      {u.description}
                                    </p>
                                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 shrink-0">
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-7 w-7"
                                        onClick={() => startEdit(u)}
                                        data-testid={`button-edit-update-${u.id}`}
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-7 w-7"
                                        onClick={() => deleteMutation.mutate(u.id)}
                                        data-testid={`button-delete-update-${u.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                            {group.fixes.length > 0 && (
                              <div className="rounded-md border bg-muted/40">
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 p-2.5 text-sm text-muted-foreground"
                                  onClick={() =>
                                    setCollapsedFixes((s) => ({ ...s, [fixKey]: !s[fixKey] }))
                                  }
                                  data-testid={`button-toggle-fixes-${week.key}-${group.key}`}
                                >
                                  {collapsedFixes[fixKey] ? (
                                    <ChevronRight className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                  <Wrench className="h-3.5 w-3.5" />
                                  {group.fixes.length} fix{group.fixes.length === 1 ? "" : "es"} &
                                  small improvements
                                </button>
                                {!collapsedFixes[fixKey] && (
                                  <ul className="px-4 pb-3 space-y-1.5">
                                    {group.fixes.map((u) => (
                                      <li
                                        key={u.id}
                                        className="group flex items-start gap-2 text-sm text-muted-foreground"
                                        data-testid={`row-fix-${u.id}`}
                                      >
                                        <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/60 shrink-0" />
                                        {editingId === u.id ? (
                                          renderEditor(u)
                                        ) : (
                                          <>
                                            <span className="flex-1">{u.description}</span>
                                            <span className="opacity-0 group-hover:opacity-100 flex gap-1 shrink-0">
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-6 w-6"
                                                onClick={() => startEdit(u)}
                                                data-testid={`button-edit-fix-${u.id}`}
                                              >
                                                <Pencil className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-6 w-6"
                                                onClick={() => deleteMutation.mutate(u.id)}
                                                data-testid={`button-delete-fix-${u.id}`}
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </span>
                                          </>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
